import { useMemo, useState } from 'react';
import { useLanguage } from '../../i18n';
import { useAuth } from '../../auth';
import CodeViewerModal from '../CodeViewerModal';

type Props = {
  isConnected: boolean;
  githubUsername?: string | null;
  repoFullName: string;
  setRepoFullName: (v: string) => void;
  onOpenModal: () => void;
  onConnect: () => void;
  selectedCommits?: { sha: string; message: string; author?: string | null; date?: string | null; html_url?: string | null }[];
  merged?: {
    focus_areas: { title: string; severity: 'high' | 'medium' | 'low'; reason: string; related_files: string[] }[];
    side_effects: string[];
    suggested_focus_areas: string[];
  } | null;
  highlights?: {
    important_symbols: {
      symbol: string;
      kind: string;
      path: string;
      reason: string;
      snippet?: { path: string; start_line: number; end_line: number; content: string } | null;
    }[];
    file_patches: { filename: string; status?: string | null; additions?: number | null; deletions?: number | null; patch?: string | null }[];
  } | null;
  hideRepoInput?: boolean;
  hideFocusAreas?: boolean;
  hideMergedSummary?: boolean; // focus/side effects 카드 전체 숨김(선택 커밋/주요 코드는 유지)
  onClearSelection?: () => void;
  isDemo?: boolean;
};

function severityBadge(sev: string) {
  switch (sev) {
    case 'high':
      return 'bg-status-nogo/15 border-status-nogo/40 text-status-nogo';
    case 'medium':
      return 'bg-status-warning/15 border-status-warning/40 text-status-warning';
    default:
      return 'bg-terminal-dim/15 border-terminal-border text-terminal-muted';
  }
}

export default function GitHubCommitReviewSection({
  isConnected,
  githubUsername,
  repoFullName,
  setRepoFullName,
  onOpenModal,
  onConnect,
  selectedCommits = [],
  merged = null,
  highlights = null,
  hideRepoInput = false,
  hideFocusAreas = false,
  hideMergedSummary = false,
  onClearSelection,
  isDemo = false,
}: Props) {
  const { lang, t } = useLanguage();
  const isKo = lang === 'ko';
  const { quota } = useAuth();

  // Max/Team 플랜 체크
  const canUseCommitFeature = quota?.plan === 'max' || quota?.plan === 'team';
  const isLockedPlan = !canUseCommitFeature;

  const canUse = isConnected && !isDemo;
  const hasSummary = canUse && selectedCommits.length > 0 && !!merged;
  const [showCode, setShowCode] = useState(false);
  const [viewer, setViewer] = useState<{
    isOpen: boolean;
    title: string;
    subtitle?: string | null;
    mode: 'text' | 'diff';
    content: string;
  }>({ isOpen: false, title: '', subtitle: null, mode: 'text', content: '' });

  const codeSummary = useMemo(() => {
    const symbols = (highlights?.important_symbols ?? []).filter(Boolean);
    const files = (highlights?.file_patches ?? []).filter(Boolean);
    return { symbols, files };
  }, [highlights]);

  return (
    <div
      id="github-commit-review-section"
      className="neo-card-static bg-terminal-bg border border-terminal-border rounded-xl p-6 mb-6 relative"
    >
      {/* 플랜 제한 오버레이 */}
      {isLockedPlan && (
        <div className="absolute inset-0 bg-terminal-bg/80 backdrop-blur-sm rounded-xl z-10 flex items-center justify-center">
          <div className="text-center p-6 max-w-md">
            <div className="text-2xl mb-2">🔒</div>
            <div className="text-sm font-semibold text-terminal-text mb-1.5">
              {isKo ? 'Max/Team 플랜 전용 기능' : 'Max/Team Plan Feature'}
            </div>
            <div className="text-xs text-terminal-muted mb-3">
              {isKo
                ? 'GitHub 커밋 변경 분석 기능은 Max 또는 Team 플랜에서만 사용할 수 있습니다.'
                : 'GitHub Commit Change Review is available on Max or Team plan only.'}
            </div>
            <div className="text-[11px] text-status-warning">
              {isKo ? '※ GitHub 연동 자체는 모든 플랜에서 가능합니다' : '※ GitHub connection is available on all plans'}
            </div>
          </div>
        </div>
      )}

      <CodeViewerModal
        isOpen={viewer.isOpen}
        onClose={() => setViewer((v) => ({ ...v, isOpen: false }))}
        title={viewer.title}
        subtitle={viewer.subtitle}
        content={viewer.content}
        mode={viewer.mode}
      />
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-base font-black text-terminal-text">
            {isKo ? 'GitHub 커밋 변경 분석' : 'GitHub Commit Change Review'}
          </div>
          <div className="text-xs text-terminal-muted mt-1">
            {canUse
              ? (githubUsername
                  ? (isKo ? `연결 계정: ${githubUsername}` : `Connected as: ${githubUsername}`)
                  : (isKo ? 'GitHub가 연결되어 있습니다.' : 'GitHub is connected.'))
              : (isDemo
                  ? (isKo ? '데모 모드에서는 사용할 수 없습니다.' : 'Not available in demo mode.')
                  : (isKo ? 'GitHub를 연결하면 사용할 수 있습니다.' : 'Connect GitHub to use this.'))}
          </div>
        </div>

        {canUse ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onOpenModal}
              disabled={isLockedPlan}
              className={`px-3 py-2 border rounded-lg text-xs font-medium transition-colors ${
                isLockedPlan
                  ? 'bg-terminal-dim/10 border-terminal-border text-terminal-muted opacity-60 cursor-not-allowed'
                  : 'bg-status-warning/15 hover:bg-status-warning/25 border-status-warning/40 text-status-warning'
              }`}
              title={isLockedPlan ? (isKo ? 'Max 또는 Team 플랜에서만 사용 가능합니다' : 'Available on Max/Team plan only') : ''}
            >
              {isLockedPlan && <span className="mr-1.5">🔒</span>}
              {hasSummary ? (isKo ? '커밋 다시 선택' : 'Change commits') : t.profile.githubCommitReviewOpen}
            </button>
            {hasSummary && onClearSelection && (
              <button
                type="button"
                onClick={onClearSelection}
                className="px-3 py-2 bg-terminal-dim/15 hover:bg-terminal-dim/25 border border-terminal-border rounded-lg text-terminal-muted text-xs font-medium transition-colors"
              >
                {isKo ? '선택 해제' : 'Clear'}
              </button>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={onConnect}
            disabled={isDemo}
            className={`px-3 py-2 border rounded-lg text-xs font-medium transition-colors ${
              isDemo
                ? 'bg-terminal-dim/15 border-terminal-border text-terminal-muted opacity-60 cursor-not-allowed'
                : 'bg-status-go/20 hover:bg-status-go/30 border border-status-go/50 text-status-go'
            }`}
          >
            {isKo ? 'GitHub 연결하기' : 'Connect GitHub'}
          </button>
        )}
      </div>

      {!hideRepoInput && (
        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="block text-xs text-terminal-muted mb-1.5">
              {isKo ? '리포지토리 (owner/repo)' : 'Repository (owner/repo)'}
            </label>
            <input
              value={repoFullName}
              onChange={(e) => setRepoFullName(e.target.value)}
              placeholder={isKo ? '예: my-org/my-repo' : 'e.g., my-org/my-repo'}
              disabled={!canUse}
              className="w-full px-3 py-2.5 bg-terminal-dim/20 border border-terminal-border rounded-lg text-terminal-text text-sm placeholder:text-terminal-muted/50 focus:outline-none focus:border-status-go/50 transition-colors disabled:opacity-60"
            />
          </div>
        </div>
      )}

      {/* 모달 외부에서도 확인 가능: 적용된 요약(중점 점검/사이드이펙트/선택 커밋) */}
      {hasSummary && (
        <div className="mt-4 space-y-3">
          <div className="text-xs text-terminal-muted">
            {isKo ? '선택된 커밋' : 'Selected commits'} ({selectedCommits.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedCommits.map((c) => (
              <a
                key={c.sha}
                href={c.html_url || undefined}
                target={c.html_url ? '_blank' : undefined}
                rel={c.html_url ? 'noreferrer' : undefined}
                className="px-2.5 py-1.5 bg-terminal-dim/15 hover:bg-terminal-dim/25 border border-terminal-border rounded-lg text-terminal-text text-xs font-mono transition-colors"
                title={c.message || c.sha}
              >
                {c.sha.slice(0, 7)}
              </a>
            ))}
          </div>

          {!hideMergedSummary && (
            <div className={`grid grid-cols-1 gap-3 ${hideFocusAreas ? '' : 'md:grid-cols-2'}`}>
              {!hideFocusAreas && (
                <div className="border border-terminal-border rounded-lg p-4 bg-terminal-dim/10 min-h-[120px]">
                  <div className="text-sm font-semibold text-terminal-text mb-2">
                    {isKo ? '중점 점검 영역' : 'Focus areas'}
                  </div>
                  {merged?.focus_areas?.length ? (
                    <div className="space-y-2">
                      {merged.focus_areas.map((f) => (
                        <div key={f.title} className="p-3 bg-terminal-dim/10 border border-terminal-border rounded-lg">
                          <div className="flex items-center gap-2">
                            <span className={`text-[11px] px-2 py-0.5 border rounded ${severityBadge(f.severity)}`}>
                              {String(f.severity).toUpperCase()}
                            </span>
                            <div className="text-sm font-semibold text-terminal-text">{f.title}</div>
                          </div>
                          <div className="text-xs text-terminal-muted mt-1 whitespace-pre-wrap">{f.reason}</div>
                          {f.related_files?.length ? (
                            <div className="text-[11px] text-terminal-muted mt-2 break-words">
                              {isKo ? '관련 파일:' : 'Related files:'}
                              <div className="mt-1 space-y-0.5 font-mono">
                                {f.related_files.slice(0, 6).map((file, idx) => (
                                  <div key={idx} className="break-all text-[10px] text-terminal-muted/80">
                                    • {file}
                                  </div>
                                ))}
                                {f.related_files.length > 6 && (
                                  <div className="text-[10px] text-terminal-muted/60 italic">
                                    {isKo ? `외 ${f.related_files.length - 6}개...` : `+${f.related_files.length - 6} more...`}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-terminal-muted">
                      {isKo ? '특별히 감지된 항목이 없습니다.' : 'No items detected.'}
                    </div>
                  )}
                </div>
              )}

              <div className="border border-terminal-border rounded-lg p-4 bg-terminal-dim/10 min-h-[120px]">
                <div className="text-sm font-semibold text-terminal-text mb-2">
                  {isKo ? '우려되는 사이드이펙트' : 'Potential side effects'}
                </div>
                {merged?.side_effects?.length ? (
                  <ul className="space-y-2">
                    {merged.side_effects.map((s) => (
                      <li key={s} className="text-sm text-terminal-text">
                        <span className="text-terminal-muted mr-2">-</span>
                        {s}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-sm text-terminal-muted">
                    {isKo ? '특별히 감지된 항목이 없습니다.' : 'No items detected.'}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="text-[11px] text-terminal-muted">
            {isKo
              ? '※ 이 요약은 테스트케이스 생성에도 자동으로 반영됩니다.'
              : '※ This summary is also used when generating suggested test cases.'}
          </div>

          {/* 주요 코드(연결된 코드 포함) */}
          {(codeSummary.symbols.length > 0 || codeSummary.files.length > 0) && (
            <div className="mt-4 border border-terminal-border rounded-lg p-4 bg-terminal-dim/10">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-terminal-text">
                    {isKo ? '주요 코드(스니펫/patch)' : 'Key code (snippets/patch)'}
                  </div>
                  <div className="text-xs text-terminal-muted mt-1">
                    {isKo
                      ? '선택한 커밋 변경과 import 연관 파일을 따라가며 중요한 함수/클래스를 자동 추출합니다. (best-effort)'
                      : 'Auto-extracts important symbols by following related imports (best-effort).'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCode((v) => !v)}
                  className="px-3 py-2 bg-terminal-dim/15 hover:bg-terminal-dim/25 border border-terminal-border rounded-lg text-terminal-text text-xs font-medium transition-colors"
                >
                  {showCode ? (isKo ? '접기' : 'Hide') : (isKo ? '보기' : 'View')}
                </button>
              </div>

              {showCode && (
                <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="border border-terminal-border rounded-lg overflow-hidden">
                    <div className="px-3 py-2 bg-terminal-dim/10 text-xs text-terminal-muted">
                      {isKo ? '중요 함수/클래스(스니펫)' : 'Important symbols (snippets)'} · {codeSummary.symbols.length}
                    </div>
                    <div className="p-3 space-y-2">
                      {codeSummary.symbols.map((s) => {
                        const key = `${s.path}::${s.kind}::${s.symbol}`;
                        const lineInfo =
                          s.snippet?.start_line && s.snippet?.end_line
                            ? `${s.snippet.start_line}-${s.snippet.end_line}`
                            : '';
                        return (
                          <div key={key} className="border border-terminal-border rounded-lg overflow-hidden">
                            <button
                              type="button"
                              className="w-full text-left px-3 py-2 bg-terminal-dim/10 hover:bg-terminal-dim/15 transition-colors flex items-center justify-between gap-3"
                              onClick={() =>
                                setViewer({
                                  isOpen: true,
                                  title: `${s.kind} ${s.symbol}`,
                                  subtitle: `${s.path}${lineInfo ? `:${lineInfo}` : ''}`,
                                  mode: 'text',
                                  content: s.snippet?.content || (isKo ? '스니펫을 생성하지 못했습니다.' : 'No snippet available.'),
                                })
                              }
                            >
                              <div className="min-w-0">
                                <div className="text-sm text-terminal-text font-semibold truncate">
                                  <span className="font-mono text-xs mr-2">{s.kind}</span>
                                  {s.symbol}
                                </div>
                                <div className="text-xs text-terminal-muted font-mono truncate">
                                  {s.path}{lineInfo ? `:${lineInfo}` : ''}
                                </div>
                              </div>
                              <div className="text-xs text-terminal-muted whitespace-nowrap min-w-[44px] text-right">
                                {isKo ? '보기' : 'View'}
                              </div>
                            </button>
                            {/* 인라인 펼침은 팝업으로 대체 */}
                          </div>
                        );
                      })}
                      {codeSummary.symbols.length === 0 && (
                        <div className="text-xs text-terminal-muted">
                          {isKo ? '중요 심볼을 찾지 못했습니다.' : 'No symbols found.'}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="border border-terminal-border rounded-lg overflow-hidden">
                    <div className="px-3 py-2 bg-terminal-dim/10 text-xs text-terminal-muted">
                      {isKo ? '변경 파일 patch(요약)' : 'Changed file patches (summary)'} · {codeSummary.files.length}
                    </div>
                    <div className="p-3 space-y-2">
                      {codeSummary.files.map((f) => {
                        const meta = `${f.status || 'unknown'} · +${f.additions ?? 0}/-${f.deletions ?? 0}`;
                        return (
                          <div key={f.filename} className="border border-terminal-border rounded-lg overflow-hidden">
                            <button
                              type="button"
                              className="w-full text-left px-3 py-2 bg-terminal-dim/10 hover:bg-terminal-dim/15 transition-colors flex items-center justify-between gap-3"
                              onClick={() =>
                                setViewer({
                                  isOpen: true,
                                  title: f.filename,
                                  subtitle: meta,
                                  mode: 'diff',
                                  content: f.patch || (isKo ? 'patch가 제공되지 않았습니다.' : 'No patch provided.'),
                                })
                              }
                            >
                              <div className="min-w-0">
                                <div className="text-sm text-terminal-text font-semibold truncate font-mono">{f.filename}</div>
                                <div className="text-xs text-terminal-muted">{meta}</div>
                              </div>
                              <div className="text-xs text-terminal-muted whitespace-nowrap min-w-[44px] text-right">
                                {isKo ? '보기' : 'View'}
                              </div>
                            </button>
                            {/* 인라인 펼침은 팝업으로 대체 */}
                          </div>
                        );
                      })}
                      {codeSummary.files.length === 0 && (
                        <div className="text-xs text-terminal-muted">
                          {isKo ? '표시할 patch가 없습니다.' : 'No patches to show.'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="text-[11px] text-terminal-muted mt-3">
        {isKo
          ? '모달에서 최근 커밋을 불러와 선택하면, 변경 파일/patch 기반으로 요약(점검 포인트/사이드이펙트/중요 코드)을 만들고 다음 단계로 넘어가기 전에 확인할 수 있어요.'
          : 'Select recent commits in the modal to build a summary (focus areas, side effects, key code) and review it before moving on.'}
      </div>
    </div>
  );
}


